import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRepertoireInfinite } from '@/lib/queries/repertoire';
import { useExportRepertoire, useImportRepertoire } from '@/lib/queries/repertoire-export';
import { useMasteryLevels, useQueueStatuses } from '@/lib/queries/reference-data';
import { FilterChips } from '@/components/filter-chips';
import { Button } from '@/components/atoms/button';
import { Micro } from '@/components/atoms/micro';
import { Icon } from '@/components/atoms/icon';
import { RepertoireList } from '@/components/organisms/repertoire-list';
import { useViewport } from '@/lib/hooks/use-viewport';
import { useDebouncedValue } from '@/lib/hooks/use-debounced-value';
import { RepertoireSplitPane } from '@/components/organisms/repertoire-split-pane';

type SortValue = `${'name' | 'mastery' | 'queueStatus'}-${'asc' | 'desc'}`;

const SORT_OPTIONS: { value: SortValue; label: string }[] = [
  { value: 'name-asc', label: 'Name A–Z' },
  { value: 'name-desc', label: 'Name Z–A' },
  { value: 'mastery-asc', label: 'Mastery (low first)' },
  { value: 'mastery-desc', label: 'Mastery (high first)' },
  { value: 'queueStatus-asc', label: 'Queue status' },
];

const SORT_VALUES: readonly SortValue[] = SORT_OPTIONS.map((o) => o.value);
const DEFAULT_SORT: SortValue = 'name-asc';

const SEARCH_DEBOUNCE_MS = 300;

// Default-valued params are omitted from the URL: validateSearch returns
// undefined for a param at its default so it does not serialize into the
// query string. The component supplies the default on read.
type RepertoireSearch = {
  q?: string;
  queue?: string;
  mastery?: string;
  sort?: SortValue;
  selected?: string;
};

export const Route = createFileRoute('/repertoire')({
  component: RepertoirePage,
  validateSearch: (raw: Record<string, unknown>): RepertoireSearch => ({
    q: typeof raw.q === 'string' && raw.q !== '' ? raw.q : undefined,
    queue: typeof raw.queue === 'string' && raw.queue !== '' ? raw.queue : undefined,
    mastery: typeof raw.mastery === 'string' && raw.mastery !== '' ? raw.mastery : undefined,
    sort:
      SORT_VALUES.includes(raw.sort as SortValue) && raw.sort !== DEFAULT_SORT
        ? (raw.sort as SortValue)
        : undefined,
    selected: typeof raw.selected === 'string' && raw.selected !== '' ? raw.selected : undefined,
  }),
});

function RepertoirePage(): ReactElement {
  const urlSearch = Route.useSearch();
  const navigate = useNavigate({ from: '/repertoire' });

  const q = urlSearch.q ?? '';
  const queueStatusFilter = urlSearch.queue ?? null;
  const masteryFilter = urlSearch.mastery ?? null;
  const sortValue = urlSearch.sort ?? DEFAULT_SORT;

  // The input value is local for keystroke responsiveness; its debounced value
  // is written to the URL, which is the single source of truth the query reads.
  const [searchInput, setSearchInput] = useState(q);
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);

  const viewport = useViewport();

  const [sort, direction] = sortValue.split('-') as [string, string];

  useEffect(() => {
    if (debouncedSearch === q) return;
    void navigate({
      search: (prev) => ({ ...prev, q: debouncedSearch === '' ? undefined : debouncedSearch }),
      replace: true,
    });
  }, [debouncedSearch, q, navigate]);

  const setQueueStatusFilter = (next: string | null): void => {
    void navigate({ search: (prev) => ({ ...prev, queue: next ?? undefined }) });
  };

  const setMasteryFilter = (next: string | null): void => {
    void navigate({ search: (prev) => ({ ...prev, mastery: next ?? undefined }) });
  };

  const setSortValue = (next: SortValue): void => {
    void navigate({ search: (prev) => ({ ...prev, sort: next }) });
  };

  const queryClient = useQueryClient();
  const exportMutation = useExportRepertoire();
  const importMutation = useImportRepertoire();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useRepertoireInfinite({
    search: debouncedSearch || undefined,
    queueStatusId: queueStatusFilter ?? undefined,
    masteryLevelId: masteryFilter ?? undefined,
    sort,
    direction,
  });
  const { data: queueStatusData } = useQueueStatuses();
  const { data: masteryData } = useMasteryLevels();

  const queueStatuses = queueStatusData?.content ?? [];
  const masteryLevels = masteryData?.content ?? [];

  const masterySortOrderById = useMemo(() => {
    const map = new Map<string, number>();
    for (const level of masteryLevels) {
      map.set(level.masteryLevelId, level.sortOrder);
    }
    return map;
  }, [masteryLevels]);

  const queueChipOptions = queueStatuses.map((qs) => ({ id: qs.queueStatusId, label: qs.prettyName }));
  const masteryChipOptions = masteryLevels.map((ml) => ({ id: ml.masteryLevelId, label: ml.prettyName }));

  const handleSearch = (value: string): void => {
    setSearchInput(value);
  };

  const handleExport = async (): Promise<void> => {
    const payload = await exportMutation.mutateAsync();
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `repertoire-export-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportFile = async (file: File): Promise<void> => {
    const text = await file.text();
    const parsed = JSON.parse(text) as { entries: { pieceName: string; arrangementName: string }[] };
    await importMutation.mutateAsync({ entries: parsed.entries });
    void queryClient.invalidateQueries({ queryKey: ['repertoire'] });
    void queryClient.invalidateQueries({ queryKey: ['practice-queue'] });
  };

  const hasFilters = Boolean(debouncedSearch || queueStatusFilter || masteryFilter);
  const totalElements = data?.pages.at(-1)?.totalElements ?? 0;
  const entries = useMemo(
    () => data?.pages.flatMap((page) => page.content) ?? [],
    [data],
  );

  // Desktop split-pane selection lives in the URL (?selected=). The effective
  // selection previews the first visible arrangement until the user picks one;
  // a `selected` id that falls out of the filtered list is dropped from the URL.
  const visibleIds = useMemo(() => entries.map((entry) => entry.arrangementId), [entries]);
  const selected = urlSearch.selected ?? null;
  const selectedIsVisible = selected !== null && visibleIds.includes(selected);
  const effectiveSelectedId = selectedIsVisible ? selected : visibleIds[0] ?? null;

  useEffect(() => {
    if (!data) return;
    if (selected !== null && !selectedIsVisible) {
      void navigate({ search: (prev) => ({ ...prev, selected: undefined }), replace: true });
    }
  }, [data, selected, selectedIsVisible, navigate]);

  const handleSelect = (arrangementId: string): void => {
    void navigate({ search: (prev) => ({ ...prev, selected: arrangementId }) });
  };

  return (
    <div className="min-h-screen bg-[var(--bg-surface)] text-[var(--ink)]">
      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <header className="border-b border-[var(--rule)] bg-[var(--bg-surface)] px-5 pt-6 pb-4 md:h-16 md:px-8 md:pt-0 md:pb-0 lg:px-10">
        <div className="mx-auto flex h-full max-w-6xl items-center justify-between gap-4">
          <div>
            <Micro className="block text-[var(--ink-3)]">Repertoire</Micro>
            <p className="font-display text-[22px] font-semibold leading-tight text-[var(--ink)] md:text-[18px]">
              {data ? `${totalElements} arrangement${totalElements === 1 ? '' : 's'}` : 'Loading…'}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void handleExport()}
              disabled={exportMutation.isPending}
              aria-label="Export repertoire"
              className="inline-flex h-9 w-9 items-center justify-center rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-soft)] text-[var(--ink-3)] transition-colors hover:border-[var(--rule-strong)] hover:text-[var(--ink)] disabled:opacity-50"
            >
              <Icon name="download" size={16} />
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={importMutation.isPending}
              aria-label="Import repertoire"
              className="inline-flex h-9 w-9 items-center justify-center rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-soft)] text-[var(--ink-3)] transition-colors hover:border-[var(--rule-strong)] hover:text-[var(--ink)] disabled:opacity-50"
            >
              <Icon name="upload" size={16} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void handleImportFile(file);
                event.target.value = '';
              }}
            />
            <Link to="/library/contribute">
              <Button size="sm">
                <Icon name="plus" size={14} /> Contribute piece
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {importMutation.isSuccess && (
        <div className="border-b border-[var(--rule)] bg-[var(--bg-surface)] px-5 py-3 md:px-8 lg:px-10">
          <p className="mx-auto max-w-6xl rounded-[var(--r-sm)] bg-[color:var(--brand-wash)] px-3 py-2 text-sm text-[var(--brand)]">
            Imported {importMutation.data.imported} entries, skipped {importMutation.data.skipped}
          </p>
        </div>
      )}

      {viewport === 'desktop' ? (
        <RepertoireSplitPane
          entries={entries}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={() => void fetchNextPage()}
          masterySortOrderById={masterySortOrderById}
          search={searchInput}
          onSearchChange={handleSearch}
          queueChipOptions={queueChipOptions}
          queueStatusFilter={queueStatusFilter}
          onQueueStatusChange={setQueueStatusFilter}
          masteryChipOptions={masteryChipOptions}
          masteryFilter={masteryFilter}
          onMasteryChange={setMasteryFilter}
          sortValue={sortValue}
          onSortChange={setSortValue}
          selectedArrangementId={effectiveSelectedId}
          onSelect={handleSelect}
        />
      ) : (
        <>
          {/* ── Search + filters (mobile/tablet) ────────────────────────── */}
          <section className="border-b border-[var(--rule)] bg-[var(--bg-surface)] px-5 py-4 md:px-8 lg:px-10">
            <div className="mx-auto flex max-w-6xl flex-col gap-3">
              <div className="flex h-11 items-center gap-2 rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-soft)] px-3 transition-colors focus-within:border-[var(--ink)]">
                <Icon name="search" size={15} className="shrink-0 text-[var(--ink-3)]" />
                <input
                  type="text"
                  placeholder="Search repertoire…"
                  value={searchInput}
                  onChange={(event) => handleSearch(event.target.value)}
                  className="flex-1 bg-transparent text-[14px] text-[var(--ink)] placeholder-[var(--ink-4)] outline-none"
                />
              </div>

              {queueChipOptions.length > 0 && (
                <div>
                  <Micro className="mb-1.5 block text-[var(--ink-3)]">Queue status</Micro>
                  <FilterChips options={queueChipOptions} selected={queueStatusFilter} onSelect={setQueueStatusFilter} />
                </div>
              )}

              {masteryChipOptions.length > 0 && (
                <div>
                  <Micro className="mb-1.5 block text-[var(--ink-3)]">Mastery level</Micro>
                  <FilterChips options={masteryChipOptions} selected={masteryFilter} onSelect={setMasteryFilter} />
                </div>
              )}
            </div>
          </section>

          {/* ── List (mobile/tablet) ───────────────────────────────────── */}
          <main className="px-5 pt-5 pb-24 md:px-8 lg:px-10 lg:pt-8">
            <div className="mx-auto max-w-6xl space-y-4">
              <div className="flex items-center justify-between">
                <Micro className="text-[var(--ink-3)]">Sort by</Micro>
                <select
                  value={sortValue}
                  onChange={(event) => setSortValue(event.target.value as SortValue)}
                  className="h-9 rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-surface)] px-3 text-[12px] font-semibold text-[var(--ink)] focus:border-[var(--ink)] focus:outline-none"
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              {isLoading && (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div
                      key={i}
                      className="animate-pulse rounded-[var(--r-md)] border border-[var(--rule)] bg-[var(--bg-surface)] p-4"
                    >
                      <div className="h-4 w-1/3 rounded bg-[var(--bg-soft)]" />
                      <div className="mt-2 h-3 w-1/4 rounded bg-[var(--bg-soft)]" />
                    </div>
                  ))}
                </div>
              )}

              {/* TODO: group by form section — see BACKLOG.md */}
              {data && (
                entries.length === 0 && hasFilters ? (
                  <div className="flex flex-col items-center justify-center rounded-[var(--r-lg)] border border-[var(--rule)] bg-[var(--bg-surface)] py-16 text-center">
                    <h3 className="font-display text-xl font-semibold text-[var(--ink)]">
                      No arrangements match your filters
                    </h3>
                    <p className="mt-2 max-w-[300px] text-sm text-[var(--ink-3)]">
                      Try clearing filters or adding a new piece to your collection.
                    </p>
                  </div>
                ) : (
                  <>
                    <RepertoireList
                      mode="navigate"
                      entries={entries}
                      masterySortOrderById={masterySortOrderById}
                    />
                    {hasNextPage && (
                      <div className="flex justify-center pt-2">
                        <Button
                          variant="secondary"
                          size="md"
                          disabled={isFetchingNextPage}
                          aria-busy={isFetchingNextPage || undefined}
                          onClick={() => void fetchNextPage()}
                        >
                          {isFetchingNextPage ? 'Loading more…' : 'Load more'}
                        </Button>
                      </div>
                    )}
                  </>
                )
              )}
            </div>
          </main>
        </>
      )}
    </div>
  );
}
